import { resolveAiReferrer, resolveAiSourceAlias, resolveOrganicSearchReferrer, type AiReferralSource, type OrganicSearchSource } from './search-referrers';

const STORAGE_KEY = 'dobidobi.web-marketing-attribution';
const IDENTIFIED_STORAGE_PREFIX = 'dobidobi.web-marketing-attribution-identified';
const DEFAULT_ATTRIBUTION_TTL_SECONDS = 90 * 24 * 60 * 60;
const identityBindings = new Map<string, Promise<boolean>>();

const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
] as const;

const CLICK_ID_PARAM_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ttclid',
  'twclid',
  'msclkid',
  'li_fat_id',
] as const;

const KNOWN_MARKETING_REFERRERS = [
  'instagram.com',
  'l.instagram.com',
  'tiktok.com',
  'vm.tiktok.com',
  'x.com',
  'twitter.com',
  't.co',
  'reddit.com',
  'old.reddit.com',
  'pinterest.com',
  'facebook.com',
  'm.facebook.com',
  'linkedin.com',
  'lnkd.in',
  'youtube.com',
  'youtu.be',
] as const;

export type MarketingAttributionSource =
  | 'utm'
  | 'paid_click_id'
  | 'social_referrer'
  | 'organic_search'
  | 'ai_referrer'
  | 'referrer_param';

export interface WebMarketingAttributionTouch {
  sourceType: MarketingAttributionSource;
  source?: OrganicSearchSource | AiReferralSource;
  medium?: string;
  landingPath: string;
  referrer: string | null;
  utm: Partial<Record<(typeof UTM_PARAM_KEYS)[number], string>>;
  clickIds: Partial<Record<(typeof CLICK_ID_PARAM_KEYS)[number], string>>;
  ref: string | null;
  capturedAt: number;
}

export interface StoredWebMarketingAttribution {
  firstTouch: WebMarketingAttributionTouch;
  lastTouch: WebMarketingAttributionTouch;
  savedAt: number;
  expiresAt: number;
}

export type WebMarketingAttributionEventType =
  | 'account_identified'
  | 'generation_started'
  | 'paid';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function normalizeParam(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function collectParams<const T extends readonly string[]>(
  searchParams: URLSearchParams,
  keys: T,
): Partial<Record<T[number], string>> {
  return keys.reduce<Partial<Record<T[number], string>>>((acc, key) => {
    const value = normalizeParam(searchParams.get(key));
    if (value) {
      acc[key as T[number]] = value;
    }
    return acc;
  }, {});
}

function hasValues(record: Record<string, string | undefined>): boolean {
  return Object.values(record).some(Boolean);
}

function getReferrerHost(referrer: string | null): string | null {
  if (!referrer) return null;

  try {
    return new URL(referrer).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return null;
  }
}

/** Authentication/payment transports are continuations, not new acquisition channels. */
export function marketingEntryReferrer(referrer: string | null): string | null {
  const host = getReferrerHost(referrer);
  if (!host || host === 'accounts.google.com' || host === 'appleid.apple.com'
    || host === 'dobidobi.ai' || host.endsWith('.dobidobi.ai')
    || host === 'airwallex.com' || host.endsWith('.airwallex.com')) return null;
  return referrer;
}

function isKnownMarketingReferrer(referrer: string | null): boolean {
  const host = getReferrerHost(referrer);
  if (!host) return false;
  return KNOWN_MARKETING_REFERRERS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

function resolveSourceType(input: {
  utm: Partial<Record<(typeof UTM_PARAM_KEYS)[number], string>>;
  clickIds: Partial<Record<(typeof CLICK_ID_PARAM_KEYS)[number], string>>;
  ref: string | null;
  referrer: string | null;
}): MarketingAttributionSource | null {
  if (hasValues(input.utm)) return 'utm';
  if (hasValues(input.clickIds)) return 'paid_click_id';
  if (input.ref) return 'referrer_param';
  if (resolveAiReferrer(input.referrer)) return 'ai_referrer';
  if (isKnownMarketingReferrer(input.referrer)) return 'social_referrer';
  if (resolveOrganicSearchReferrer(input.referrer)) return 'organic_search';
  return null;
}

export function loadStoredWebMarketingAttribution(): StoredWebMarketingAttribution | null {
  if (!isBrowser()) return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredWebMarketingAttribution;
    if (!parsed.expiresAt || parsed.expiresAt <= Date.now() || !parsed.firstTouch || !parsed.lastTouch) {
      clearStoredWebMarketingAttribution();
      return null;
    }
    return parsed;
  } catch {
    clearStoredWebMarketingAttribution();
    return null;
  }
}

export function clearStoredWebMarketingAttribution(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage restrictions must not prevent the visitor from using the page.
  }
}

export function buildWebMarketingAttributionTouch(
  searchParams: URLSearchParams,
  landingPath: string,
  referrer?: string | null,
): WebMarketingAttributionTouch | null {
  const utm = collectParams(searchParams, UTM_PARAM_KEYS);
  const clickIds = collectParams(searchParams, CLICK_ID_PARAM_KEYS);
  const ref = normalizeParam(searchParams.get('ref'));
  const normalizedReferrer = normalizeParam(referrer ?? null);
  const sourceType = resolveSourceType({ utm, clickIds, ref, referrer: normalizedReferrer });

  if (!sourceType) return null;

  const aiSource = sourceType === 'ai_referrer'
    ? resolveAiReferrer(normalizedReferrer)
    : sourceType === 'utm' ? resolveAiSourceAlias(utm.utm_source ?? null) : null;

  return {
    sourceType,
    ...(aiSource ? {
      source: aiSource,
      medium: utm.utm_medium?.trim().toLowerCase() || 'referral',
    } : {}),
    ...(sourceType === 'organic_search' ? {
      source: resolveOrganicSearchReferrer(normalizedReferrer)!,
      medium: 'organic' as const,
    } : {}),
    landingPath,
    referrer: normalizedReferrer,
    utm,
    clickIds,
    ref,
    capturedAt: Date.now(),
  };
}

export function saveWebMarketingAttributionTouch(
  touch: WebMarketingAttributionTouch,
  attributionTtlSeconds = DEFAULT_ATTRIBUTION_TTL_SECONDS,
): StoredWebMarketingAttribution | null {
  if (!isBrowser()) return null;

  const now = Date.now();
  const existing = loadStoredWebMarketingAttribution();
  // Authentication, template selection and retained UTM parameters do not
  // create a new marketing touch. Preserve the original landing and clock.
  const identity = (value: WebMarketingAttributionTouch) => JSON.stringify([
    value.sourceType, value.utm, value.clickIds, value.ref, value.source, value.medium,
  ]);
  if (existing && now - existing.savedAt < 30 * 60 * 1000
    && identity(existing.lastTouch) === identity(touch)) return existing;
  const nextValue: StoredWebMarketingAttribution = {
    firstTouch: existing?.firstTouch ?? touch,
    lastTouch: touch,
    savedAt: now,
    expiresAt: now + attributionTtlSeconds * 1000,
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextValue));
    return nextValue;
  } catch {
    return null;
  }
}

export function captureWebMarketingAttributionFromUrl(
  searchParams: URLSearchParams,
  landingPath: string,
  referrer?: string | null,
): StoredWebMarketingAttribution | null {
  const touch = buildWebMarketingAttributionTouch(searchParams, landingPath, referrer);
  return touch ? saveWebMarketingAttributionTouch(touch) : null;
}

export async function trackWebMarketingEventIfAttributed(
  eventType: WebMarketingAttributionEventType,
  metadata?: Record<string, unknown>,
): Promise<boolean> {
  const attribution = loadStoredWebMarketingAttribution();
  if (!attribution) return false;

  const { trackSignalWithAck } = await import('@/lib/api/signals');
  return trackSignalWithAck('marketing_attribution', {
    event_type: eventType,
    first_touch: attribution.firstTouch,
    last_touch: attribution.lastTouch,
    attribution_saved_at: attribution.savedAt,
    attribution_expires_at: attribution.expiresAt,
    ...metadata,
  });
}

export async function bindWebMarketingAttributionIfNeeded(userId: string): Promise<boolean> {
  const attribution = loadStoredWebMarketingAttribution();
  if (!attribution || !userId || !isBrowser()) return false;

  const markerKey = `${IDENTIFIED_STORAGE_PREFIX}:${userId}`;
  const markerValue = `${attribution.firstTouch.capturedAt}:${attribution.lastTouch.capturedAt}`;
  try {
    if (window.localStorage.getItem(markerKey) === markerValue) return true;
  } catch {
    // Storage restrictions should not prevent a best-effort identity binding.
  }

  const key = `${markerKey}:${markerValue}`;
  const inFlight = identityBindings.get(key);
  if (inFlight) return inFlight;
  const attempt = (async () => {
    const [{ trackSignalWithAck }, { useAuthStore }] = await Promise.all([
      import('@/lib/api/signals'), import('@/stores/authStore'),
    ]);
    const isCurrentOwner = () => {
      const state = useAuthStore.getState();
      return state.isAuthenticated && state.user?.id === userId && !state.user.isAdmin;
    };
    const acknowledged = await trackSignalWithAck('marketing_attribution', {
      event_type: 'account_identified',
      first_touch: attribution.firstTouch,
      last_touch: attribution.lastTouch,
      client_surface: 'web',
    }, undefined, isCurrentOwner);
    if (!acknowledged) return false;
    try { window.localStorage.setItem(markerKey, markerValue); }
    catch { /* Backend binding succeeded; a later page may safely retry. */ }
    return true;
  })();
  identityBindings.set(key, attempt);
  try { return await attempt; }
  catch { return false; }
  finally { if (identityBindings.get(key) === attempt) identityBindings.delete(key); }
}
